import { memo, ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { Warning as WarningIcon, Info as InfoIcon, Error as ErrorIcon } from '@mui/icons-material';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Simple text message - use this OR children, not both */
  message?: string;
  /** Rich content - use this OR message, not both */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  /** Optional icon to display next to the title */
  icon?: 'warning' | 'info' | 'error' | null;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const iconMap = {
  warning: <WarningIcon color="warning" />,
  info: <InfoIcon color="info" />,
  error: <ErrorIcon color="error" />,
};

/**
 * Reusable confirmation dialog component
 * Supports both simple text messages and rich content via children
 */
export const ConfirmDialog = memo<ConfirmDialogProps>(({
  open,
  title,
  message,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'primary',
  icon = null,
  isLoading = false,
  onConfirm,
  onCancel,
}) => (
  <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
    <DialogTitle>
      {icon ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {iconMap[icon]}
          <Typography variant="h6" component="span">{title}</Typography>
        </Box>
      ) : (
        title
      )}
    </DialogTitle>
    <DialogContent>
      {children ? (
        <Box>{children}</Box>
      ) : message ? (
        <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{message}</DialogContentText>
      ) : null}
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={isLoading}>
        {cancelLabel}
      </Button>
      <Button
        onClick={onConfirm}
        color={confirmColor}
        variant="contained"
        disabled={isLoading}
      >
        {isLoading ? 'Processing...' : confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
));

ConfirmDialog.displayName = 'ConfirmDialog';

export default ConfirmDialog;
